import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { Banner } from '../components/BannerModalContent';
import BannerModalContent from '../components/BannerModalContent';
import Button, { ButtonWithLoadingState } from '../components/Button';
import type { ImageEditorRootRef } from '../components/ImageEditor';
import ImageEditor, { IMAGE_EDITOR_ACCEPT } from '../components/ImageEditor';
import { POST_BANNER_IMAGE_LIBRARY } from '../components/imageLibraries';
import { EditorActions } from '../editor/editorSlice';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import PostLayout from './PostLayout';
import * as styles from './PostViewBannerEditor.module.scss';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import useLayoutSize, { LayoutSize } from './utils/useLayoutSize';

// We multiply width and height by 2 to save higher resolution
// images that look good on retina displays.
const BANNER_HEIGHT = parseInt(styles.bannerHeight, 10) * 2;
const BANNER_WIDTH = parseInt(styles.bannerWidth, 10) * 2;

const POST_FRAGMENT = gql(`
  fragment PostViewBannerEditor_post on Post {
    id
    externalPost {
      id
    }
    ...usePostPolicyCheck_post
  }
`);

const UPDATE_POST_BANNER_MUTATION = gql(/** @complexity 4 */ `
  mutation updatePostBanner(
    $postId: ID!
    $banner: Upload
    $unsplashId: String
  ) {
    updatePost(id: $postId, banner: $banner, unsplashId: $unsplashId) {
      id
      banner {
        original
      }
    }
  }
`);

interface PostViewBannerEditorProps {
  currentBannerUrl: string | null;
  banner: Banner;
  post: DocumentType<typeof POST_FRAGMENT>;
  setInnerBannerRef: React.Ref<HTMLDivElement>;
}

const PostViewBannerEditor = ({
  currentBannerUrl,
  banner,
  post,
  setInnerBannerRef: innerBannerRef,
}: PostViewBannerEditorProps) => {
  const imageEditorRef = useRef<ImageEditorRootRef>(null);

  const [hasPositionChanged, setHasPositionChanged] = useState(false);
  useEffect(() => {
    setHasPositionChanged(false);
  }, [banner]);

  const dispatch = useAppDispatch();
  const layoutSize = useLayoutSize();
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const currentUser = useAppSelector((state) => state.session.user);

  const isPostBannerPopOverOpen = useAppSelector(
    (state) => state.editor.isPostBannerPopOverOpen,
  );
  const isPositioningPostBanner = useAppSelector(
    (state) => state.editor.isPositioningPostBanner,
  );

  const postPolicyCheck = usePostPolicyCheck();
  const canEditPost = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const isDisabled =
    layoutSize === LayoutSize.mobile ||
    (post.externalPost ? !canEditPost : readOnly);

  const isDesktop = layoutSize === LayoutSize.desktop;

  const setIsPostBannerPopOverOpen = (open: boolean) => {
    dispatch(EditorActions.setIsPostBannerPopOverOpen(open));
  };

  const setIsPositioningPostBanner = useCallback(
    (isPositioning: boolean) => {
      dispatch(EditorActions.setIsPositioningPostBanner(isPositioning));
    },
    [dispatch],
  );

  const [updatePostBanner] = useMutation(UPDATE_POST_BANNER_MUTATION);

  const uploadBannerFile = useCallback(
    async (file: File) => {
      await updatePostBanner({
        variables: {
          postId: post.id,
          banner: file,
          unsplashId: banner.unsplashId,
        },
      });
    },
    [updatePostBanner, post.id, banner.unsplashId],
  );

  const uploadCurrentBanner = useCallback(async () => {
    const bannerFile = await imageEditorRef.current?.getFile();

    if (bannerFile) {
      await uploadBannerFile(bannerFile);
    }
  }, [uploadBannerFile]);

  useEffect(() => {
    if (hasPositionChanged && readOnly) {
      uploadCurrentBanner();
    }
  }, [readOnly, hasPositionChanged, uploadCurrentBanner]);

  const removeBanner = async () => {
    await updatePostBanner({
      variables: { postId: post.id, banner: null, unsplashId: null },
    });
  };

  const handleBannerChange = async (newBanner: Banner | null) => {
    if (newBanner) {
      setIsPositioningPostBanner(true);
    } else {
      await removeBanner();
      setIsPostBannerPopOverOpen(false);
    }
    dispatch(EditorActions.setCurrentPostBanner(newBanner));
  };

  const [isSavingPosition, setIsSavingPosition] = useState(false);

  const handleSavePosition = async () => {
    setIsSavingPosition(true);
    await uploadCurrentBanner().finally(() => {
      setIsSavingPosition(false);
    });
    setIsPositioningPostBanner(false);
    setHasPositionChanged(false);
  };

  return (
    <>
      <PostLayout.Banner
        className={classNames(styles.bannerContainer, {
          [styles.isEditing]: !isDisabled,
          [styles.shouldShowControls]:
            isPostBannerPopOverOpen || hasPositionChanged,
        })}
      >
        <div className={styles.banner} ref={innerBannerRef}>
          <ImageEditor.Root
            src={currentBannerUrl}
            value={banner}
            positioning={isPositioningPostBanner}
            ref={imageEditorRef}
            height={BANNER_HEIGHT}
            width={BANNER_WIDTH}
            onImageLoad={(file) => {
              if (file) {
                uploadBannerFile(file);
              }
            }}
            onScaleChange={() => setHasPositionChanged(true)}
            onPositionChange={() => setHasPositionChanged(true)}
            onDropImage={(image) => handleBannerChange({ image })}
            dropZoneDisabled={isDisabled}
            onDropZoneClick={() => {
              if (!isPositioningPostBanner) {
                setIsPostBannerPopOverOpen(!isPostBannerPopOverOpen);
              }
            }}
          />

          {isPositioningPostBanner && isDesktop && (
            <div className={styles.dragZoomInfoPillContainer}>
              <ImageEditor.DragZoomInfoPill />
            </div>
          )}
        </div>
      </PostLayout.Banner>

      {!isDisabled && (
        <PostLayout.Controls className={styles.bannerControlsContainer}>
          <div className={styles.bannerControls}>
            {isPositioningPostBanner && hasPositionChanged && (
              <ButtonWithLoadingState
                variant="secondary"
                onClick={handleSavePosition}
                size="small"
                className={styles.savePositionButton}
                loading={isSavingPosition}
              >
                Save position
              </ButtonWithLoadingState>
            )}

            <ImageEditor.PopOver
              placement={isDesktop ? 'bottom' : 'bottom-end'}
              content={
                <BannerModalContent
                  onRemove={() => handleBannerChange(null)}
                  onBannerChange={handleBannerChange}
                  uploaderProps={{
                    accept: IMAGE_EDITOR_ACCEPT,
                    info: '16:9 ratio is recommended',
                  }}
                  imageLibrary={POST_BANNER_IMAGE_LIBRARY}
                  displayMode="grid"
                />
              }
              open={isPostBannerPopOverOpen}
              onOpenChange={setIsPostBannerPopOverOpen}
            >
              <Button
                variant="secondary"
                onClick={() => setIsPostBannerPopOverOpen(true)}
                size="small"
              >
                Edit cover
              </Button>
            </ImageEditor.PopOver>
          </div>
        </PostLayout.Controls>
      )}
    </>
  );
};

export default PostViewBannerEditor;
